import { Badge } from '@chopchop-se/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Days from today at which a best-before date starts shouting. */
export const EXPIRY_WARNING_DAYS = 7;

export function daysUntil(date: string, now = Date.now()): number {
  return Math.floor((new Date(`${date}T00:00:00Z`).getTime() - now) / 86_400_000);
}

/**
 * Best-before as a colour, not a date.
 *
 * A warehouse reads expiry by scanning a column for red; the exact day is the
 * second question. Used by the stock, lot and stocktake lists so "expired"
 * looks identical everywhere.
 */
export function ExpiryCell({ date }: { date: string | null }): ReactNode {
  const { t } = useTranslation('warehouseCore');
  if (!date) return <span className="text-ink-muted">—</span>;

  const days = daysUntil(date);
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge tone="danger">{t('lots.expired')}</Badge>
        <span className="font-mono text-xs text-ink-muted">{date}</span>
      </span>
    );
  }
  if (days <= EXPIRY_WARNING_DAYS) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge tone="warning">{t('lots.expiresSoon')}</Badge>
        <span className="font-mono text-xs text-ink-muted">{date}</span>
      </span>
    );
  }
  return <span className="font-mono text-sm">{date}</span>;
}
